import React, { useCallback } from "react";
import styled from "@emotion/styled";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import ReactJson from "react-json-view";
// import Tooltip from "@mui/material/Tooltip";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Article as ArticleIcon,
  ExpandMore as ExpandMoreIcon,
  RemoveRedEye as RemoveRedEyeIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";

import { Download as DownloadIcon } from "react-feather";
import BackIcon from "@mui/icons-material/ArrowBackOutlined";
import ReactCountryFlag from "react-country-flag";

import useLLMEvent from "../../../hooks/user/useLLMEvent";
import useAuth from "../../../hooks/useAuth";
import { formatDate } from "../../../utils/format";

import { SnackbarAlert } from "../../../components/pages/common/styled";
import {
  downloadAsPdf,
  downloadObjectAsJson,
  getRedactedStatusString,
  KeyValueComponent,
  TabPanel,
  TitleComponent,
} from "../../../components/pages/application/analytics/common";

import { ReactComponent as TimelineIcon } from "../../../vendor/button/timeline.svg";
import { ReactComponent as RawdataIcon } from "../../../vendor/button/raw_data.svg";
import Speedometer from "../../../components/pages/application/analytics/Speedometer";
import LLMScannersTable from "../../../components/pages/llm/analytics/T_ScannerStatus";

const Root = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
`;

function LLMAnalyticsEventsDetail() {
  const theme = useTheme();

  const navigate = useNavigate();
  const { eventID } = useParams();
  const { isAuthenticated } = useAuth();
  const { getEvent, event, errMsg, setErr } = useLLMEvent();
  const [request_headers, setRequestHeaders] = React.useState([]);
  const [response_headers, setResponseHeaders] = React.useState([]);

  const [tabIndex, setTabeIndex] = React.useState(0);

  // const handleTabIndexChange = (e, newValue) => {
  //   setTabeIndex(newValue);
  // };

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getEvent(eventID);
    }
    return () => setErr(null);
  }, [isAuthenticated, setErr, getEvent, eventID]);

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const handleSnackClose = () => {
    setSnackOpen(false);
    setErr(null);
  };

  React.useEffect(() => {
    if (errMsg) {
      setSnackOpen(true);
      setMessage(errMsg);
      setSuccess("error");
    } else {
      setSnackOpen(false);
      setMessage(null);
    }
  }, [errMsg]);

  React.useEffect(() => {
    var tmpHeader = [];
    if (event?.request_headers) {
      Object.keys(event?.request_headers).forEach(function (key) {
        tmpHeader.push({ key: key, value: event?.request_headers[key] });
      });
    }
    setRequestHeaders(tmpHeader);
    tmpHeader = [];
    if (event?.response_headers) {
      Object.keys(event?.response_headers).forEach(function (key) {
        tmpHeader.push({ key: key, value: event?.response_headers[key] });
      });
    }
    setResponseHeaders(tmpHeader);
  }, [event]);

  const onBackPressed = () => {
    // navigate("/llm/analytics/events");
    navigate(-1);
  };

  const downloadEventDetail = () => {
    const curDate = new Date();
    downloadAsPdf("eventDetail", `Layerd Signature LLM events (${formatDate(curDate)}).pdf`);
  };

  const handleDownloadClick = (e) => {
    e.preventDefault();
    if (0 === tabIndex) {
      downloadEventDetail();
    } else if (1 === tabIndex) {
      downloadObjectAsJson(event?.raw, "SD_RawEventData");
    }
  };
  const getRedactStatusColor = useCallback(
    (res_status) => {
      switch (res_status) {
        case "Redacted":
          return theme.palette.custom.red.main;
        case "Passed":
          return theme.palette.green.main;
        default:
          return theme.palette.grey.main;
      }
    },
    [theme.palette]
  );

  const getRedactStatusBackground = useCallback(
    (res_status) => {
      switch (res_status) {
        case "Redacted":
          return theme.palette.custom.red.opacity_10;
        case "Passed":
          return theme.palette.custom.green.opacity_20;
        default:
          return theme.palette.grey.main;
      }
    },
    [theme.palette]
  );

  return (
    <React.Fragment>
      <Helmet title="LLM Event Detail" />
      <Grid container spacing={6} mt={9} sx={{ display: "flex", alignItems: "center" }}>
        <Grid item>
          <Typography variant="h1" gutterBottom display="inline">
            LLM Event Detail
          </Typography>
        </Grid>
        <Grid item xs></Grid>
        <Grid item display="flex" alignItems="center">
          <Button variant="text" startIcon={<DownloadIcon />} onClick={handleDownloadClick}>
            Download
          </Button>
          <Button variant="contained" color="warning" size="ui" sx={{ marginLeft: 4 }} startIcon={<BackIcon />} onClick={onBackPressed}>
            Back
          </Button>
        </Grid>
      </Grid>
      {event === null ? (
        <>
          <Root mt={8}>
            <CircularProgress color="primary" />
          </Root>
        </>
      ) : (
        <>
          <Grid container mt={8} spacing={2}>
            <Grid item xs={6}>
              <Button
                variant={tabIndex === 1 ? "outlined" : "contained"}
                color="tab"
                onClick={() => setTabeIndex(0)}
                fullWidth
                sx={{ height: "106px", borderWidth: "0px", borderRadius: "8px", padding: 1 }}
              >
                <Grid container>
                  <Grid xs={12}>
                    <TimelineIcon />
                  </Grid>
                  <Grid xs={12}>
                    <Typography variant="h2" pl="8px" sx={{ fontSize: "15px" }}>
                      Timeline
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant={tabIndex === 0 ? "outlined" : "contained"}
                color="tab"
                onClick={() => setTabeIndex(1)}
                fullWidth
                sx={{ height: "106px", borderWidth: "0px", borderRadius: "8px", padding: 1 }}
              >
                <Grid container>
                  <Grid xs={12}>
                    <RawdataIcon />
                  </Grid>
                  <Grid xs={12}>
                    <Typography variant="h2" pl="8px" sx={{ fontSize: "15px" }}>
                      Raw data
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            </Grid>
          </Grid>
          <TabPanel tabIndex={tabIndex} index={0} p={0}>
            <Stack id="eventDetail" pt={7} direction="column" spacing={2.5}>
              <Accordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Grid container display="flex" alignItems="center">
                    <Grid item xs={3} display="flex" alignItems="center">
                      <ArticleIcon sx={{ mr: 4 }} />
                      <Typography variant="h2">Activity Log</Typography>
                    </Grid>
                    <Typography ml={4} variant="h3">
                      {event?.method}
                    </Typography>
                    <Typography ml={2} variant="h3">
                      {event?.uri}
                    </Typography>
                    <Grid item xs />
                    <Grid item pr={2}>
                      {event?.timestamp ? formatDate(event?.timestamp) : "-"}
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container px={3.5} pb={4}>
                    <KeyValueComponent keyWidth={100} keyStr={"Host Name"} valueStr={event?.host_name} />
                    <Grid item xs={12} display="flex" alignItems={"center"} pl={4} mt={4}>
                      <Typography sx={{ width: "100px", wordBreak: "break-all", "text-transform": "capitalize" }}>Source IP</Typography>
                      <Box ml={3.5} display="flex" alignItems={"center"}>
                        <ReactCountryFlag countryCode={event?.country_iso_code} svg title={event?.country_name} />
                        <Typography ml={2}>{event?.src_ip || "-"}</Typography>
                      </Box>
                    </Grid>
                    {/* <KeyValueComponent keyStr={"Destination IP"} valueStr={event?.dst_ip} /> */}
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Grid container display="flex" alignItems="center">
                    <Grid item xs={3} display="flex" alignItems="center">
                      <ArrowForwardIcon sx={{ mr: 4 }} />
                      <Typography variant="h2">Request</Typography>
                    </Grid>
                    <Typography ml={4} variant="h3">
                      <Stack
                        direction="row"
                        spacing={1.5}
                        p={2.5}
                        alignItems="center"
                        sx={{ background: getRedactStatusBackground(getRedactedStatusString(event?.prompt_redacted)), borderRadius: "8px" }}
                      >
                        <Box
                          style={{
                            width: 10,
                            height: 10,
                            backgroundColor: getRedactStatusColor(getRedactedStatusString(event?.prompt_redacted)),
                            borderRadius: 100,
                          }}
                        />
                        <Typography variant="textMedium">{getRedactedStatusString(event?.prompt_redacted)}</Typography>
                      </Stack>
                    </Typography>
                    <Grid item xs />
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container px={3.5} pb={4}>
                    <KeyValueComponent keyStr={"Path"} valueStr={event?.uri} keyWidth={144} />
                    <KeyValueComponent keyStr={"Method"} valueStr={event?.method} keyWidth={144} />
                    <KeyValueComponent keyStr={"Host"} valueStr={event?.host_name} keyWidth={144} />
                    <KeyValueComponent keyStr={"User Agent"} valueStr={event?.ua} keyWidth={144} />
                    <TitleComponent title="Headers" />
                    {request_headers?.map((h) => {
                      return <KeyValueComponent keyStr={h?.key} valueStr={h?.value} keyWidth={144} />;
                    })}
                    <TitleComponent title="Query" />
                    {event?.query?.map((h) => {
                      return <KeyValueComponent keyStr={h?.key} valueStr={h?.value} keyWidth={144} />;
                    })}
                    <TitleComponent title="Original Request Body" />
                    <Grid item xs={12} display="flex" alignItems={"center"}>
                      <Box mt={2} px={4} py={7}>
                        <ReactJson name="raw" src={event?.prompt?.origin} displayDataTypes={false} style={{ wordBreak: "break-word" }} />
                      </Box>
                    </Grid>
                    <TitleComponent title="Redacted Request Body" />
                    <Grid item xs={12} display="flex" alignItems={"center"}>
                      <Box mt={2} px={4} py={7}>
                        <ReactJson name="raw" src={event?.prompt?.modified} displayDataTypes={false} style={{ wordBreak: "break-word" }} />
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <SecurityIcon sx={{ mr: 4 }} />
                  <Typography variant="h2">LLM Detection</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container px={3.5} pb={4}>
                    <Grid item xs={12} md={6} display="flex" alignItems={"center"}>
                      <Stack width="90%">
                        <Typography
                          sx={{
                            wordBreak: "break-all",
                          }}
                          variant="h2"
                          mt={4}
                        >
                          <b>Prompt Scanners</b>
                        </Typography>
                        <LLMScannersTable
                          resultsScore={event?.prompt?.results_score?.[0] ?? {}}
                          resultsValid={event?.prompt?.results_valid?.[0] ?? {}}
                          configs={event?.prompt?.config ?? {}}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6} display="flex" alignItems={"center"}>
                      <Stack width="90%">
                        <Typography
                          sx={{
                            wordBreak: "break-all",
                          }}
                          variant="h2"
                          mt={4}
                        >
                          <b>Output Scanners</b>
                        </Typography>
                        <LLMScannersTable
                          resultsScore={event?.output?.results_score?.[0] ?? {}}
                          resultsValid={event?.output?.results_valid?.[0] ?? {}}
                          configs={event?.output?.config ?? {}}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center">
                    <ArrowBackIcon sx={{ mr: 4 }} />
                    <Typography variant="h2">Response</Typography>
                    <Typography pl={4}>{event?.resStatus}</Typography>
                    <Typography ml={4} variant="h3">
                      <Stack
                        direction="row"
                        spacing={1.5}
                        p={2.5}
                        alignItems="center"
                        sx={{ background: getRedactStatusBackground(getRedactedStatusString(event?.output_redacted)), borderRadius: "8px" }}
                      >
                        <Box
                          style={{
                            width: 10,
                            height: 10,
                            backgroundColor: getRedactStatusColor(getRedactedStatusString(event?.output_redacted)),
                            borderRadius: 100,
                          }}
                        />
                        <Typography variant="textMedium">{getRedactedStatusString(event?.output_redacted)}</Typography>
                      </Stack>
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container px={3.5} pb={4}>
                    <KeyValueComponent keyStr={"Response Status"} valueStr={event?.resStatus} />
                    <TitleComponent title="Headers" />
                    {response_headers?.map((h) => {
                      return <KeyValueComponent keyStr={h?.key} valueStr={h?.value} />;
                    })}
                    <TitleComponent title="Original Response Body" />
                    <Box mt={2} px={4} py={7}>
                      <ReactJson name="raw" src={event?.output?.origin} displayDataTypes={false} style={{ wordBreak: "break-word" }} />
                    </Box>
                    <TitleComponent title="Redacted Response Body" />
                    <Box mt={2} px={4} py={7}>
                      <ReactJson name="raw" src={event?.output?.modified} displayDataTypes={false} style={{ wordBreak: "break-word" }} />
                    </Box>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <RemoveRedEyeIcon sx={{ mr: 4 }} />
                  <Typography variant="h2">IP Reputation</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ py: 4 }}>
                  {/* {event.otx?.whois && (
                                        <Grid container spacing={6}>
                                            <Grid item xs={12} md={6}>
                                                <Grid container spacing={6}>
                                                    <Grid item xs={4}>
                                                        <Typography
                                                            variant="h2"
                                                            pr={4}
                                                        >
                                                            Whois
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <a
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            href={                                                                event.otx?.whois
                                                            }
                                                        >
                                                            {event.otx?.whois}
                                                        </a>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}
                                    {0 < event.otx?.pulse_info?.count && (
                                        <Grid container spacing={6} pt={6}>
                                            <Grid item xs={12} md={8}>
                                                <Grid container spacing={6}>
                                                    <Grid item xs={3}>
                                                        <Typography variant="h2">
                                                            Pulses
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={9}>
                                                        {event.otx?.pulse_info?.pulses?.map(
                                                            (pulse, idx) => {                                                                if (4 < idx)
                                                                    return (
                                                                        <></>
                                                                    );
                                                                return (
                                                                    <Typography
                                                                        pb={2}
                                                                    >
                                                                        {                                                                            pulse?.name
                                                                        }
                                                                    </Typography>
                                                                );
                                                            }
                                                        )}
                                                        {event.otx?.pulse_info
                                                            ?.count > 5 && (
                                                            <Tooltip
                                                                title={`Count: ${event.otx?.pulse_info?.count}`}
                                                            >
                                                                <Typography
                                                                    sx={{                                                                        width: "fit-content",
                                                                    }}
                                                                >
                                                                    More ...
                                                                </Typography>
                                                            </Tooltip>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )} */}
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <Grid container>
                        <Grid item xs={12} display="flex" alignItems={"center"} pl={4} pt={4}>
                          <Typography sx={{ width: "210px", wordBreak: "break-all", "text-transform": "capitalize" }}>
                            IP Address
                          </Typography>
                          {event?.abuseip?.countryCode && (
                            <Box ml={4} display="flex" alignItems={"center"}>
                              <ReactCountryFlag
                                countryCode={event?.abuseip?.countryCode}
                                svg
                                title={event?.abuseip?.countryName || event?.abuseip?.countryCode}
                              />
                              <Typography ml={2}>{event?.abuseip?.ipAddress || "-"}</Typography>
                            </Box>
                          )}
                        </Grid>
                        {undefined !== event?.abuseip?.abuseConfidenceScore && null !== event?.abuseip?.abuseConfidenceScore && (
                          <KeyValueComponent
                            keyStr={"Abuse Confidence Score"}
                            valueStr={event?.abuseip?.abuseConfidenceScore?.toString()}
                          />
                        )}
                        {event?.abuseip?.usageType && <KeyValueComponent keyStr={"Usage Type"} valueStr={event?.abuseip?.usageType} />}
                        {event?.abuseip?.isp && <KeyValueComponent keyStr={"ISP"} valueStr={event?.abuseip?.isp} />}
                        {undefined !== event?.abuseip?.totalReports && null !== event?.abuseip?.totalReports && (
                          <KeyValueComponent keyStr={"Total Reports"} valueStr={event?.abuseip?.totalReports?.toString()} />
                        )}
                        {event?.abuseip?.reports?.map((report, idx) => (
                          <>
                            <TitleComponent title={`Report ${idx + 1}`} />
                            {report.reportedAt && <KeyValueComponent keyStr={"Reported Date"} valueStr={formatDate(report.reportedAt)} />}
                            <KeyValueComponent keyStr={"Comment"} valueStr={report.comment} />
                            {0 < report.categories?.length && (
                              <KeyValueComponent keyStr={"Categories"} valueStr={report.categories.join()} />
                            )}
                            <Grid item xs={12} display="flex" alignItems={"center"}>
                              <Typography
                                sx={{ width: "210px", textAlign: "right", wordBreak: "break-all", "text-transform": "capitalize" }}
                              >
                                Reporter ID
                              </Typography>
                              {report?.reporterCountryCode && (
                                <Box ml={4} display="flex" alignItems={"center"}>
                                  <ReactCountryFlag
                                    countryCode={report.reporterCountryCode}
                                    svg
                                    title={report.reporterCountryName || report.reporterCountryCode}
                                  />
                                  <Typography ml={2}>{report.reporterId || "-"}</Typography>
                                </Box>
                              )}
                            </Grid>
                          </>
                        ))}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={6} px={4}>
                      <Box
                        sx={{
                          width: "100%",
                          paddingY: "14px",
                          background: theme.palette.custom.white.bglight,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box m={"auto"} textAlign={"center"}>
                          <Typography variant="textSemiBold" pb={2}>
                            Abuse confident score
                          </Typography>
                          <Speedometer value={event?.abuseip?.abuseConfidenceScore ?? 0} />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </TabPanel>
          <TabPanel tabIndex={tabIndex} index={1}>
            <Box mt={7} px={4} py={7} sx={{ background: "white", borderRadius: 2 }}>
              <ReactJson name="raw" src={event?.raw} displayDataTypes={false} style={{ wordBreak: "break-word" }} />
            </Box>
          </TabPanel>
        </>
      )}
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default LLMAnalyticsEventsDetail;
